import { useRef, type ReactNode } from "react";
import classNames from "classnames";
import { Link } from "@design-system/Link/Link";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    isOverflowingX,
    useTruncationTooltip,
} from "@design-system/Tooltip/useTruncationTooltip";
import { ChevronRightSmallIcon } from "@icons";
import type {
    BreadcrumbItemProps,
    BreadcrumbsProps,
} from "./Breadcrumbs.types";
import styles from "./Breadcrumbs.module.scss";

export type {
    BreadcrumbItemProps,
    BreadcrumbsProps,
} from "./Breadcrumbs.types";

/**
 * Trail of places above the current page (Figma `Breadcrumb`, node 4028:1486).
 * Compose it from `BreadcrumbItem` children; each item draws its own separator
 * and the stylesheet hides the last one.
 */
export const Breadcrumbs = ({
    children,
    "aria-label": ariaLabel = "Breadcrumb",
    className,
}: BreadcrumbsProps) => (
    <nav
        aria-label={ariaLabel}
        className={classNames(styles.breadcrumbs, className)}
    >
        <ol className={styles.list}>{children}</ol>
    </nav>
);

/** Non-interactive level: optional icon, then a label that can truncate. */
const BreadcrumbText = ({
    leftIcon,
    children,
}: {
    leftIcon: ReactNode;
    children: ReactNode;
}) => (
    <span className={styles.text}>
        {leftIcon == null ? null : (
            <span className={styles.icon}>{leftIcon}</span>
        )}
        {children}
    </span>
);

/** The current page's label: truncates, and shows the full text only while cut off. */
const CurrentBreadcrumb = ({
    leftIcon,
    children,
}: {
    leftIcon: ReactNode;
    children: ReactNode;
}) => {
    const labelRef = useRef<HTMLSpanElement>(null);
    const truncationTooltipProps = useTruncationTooltip({
        ref: labelRef,
        isTruncated: isOverflowingX,
    });

    return (
        <BreadcrumbText leftIcon={leftIcon}>
            <Tooltip
                content={children}
                side="bottom"
                {...truncationTooltipProps}
            >
                <span
                    ref={labelRef}
                    className={styles.label}
                    aria-current="page"
                >
                    {children}
                </span>
            </Tooltip>
        </BreadcrumbText>
    );
};

const BreadcrumbItemContent = ({ item }: { item: BreadcrumbItemProps }) => {
    if (item.current) {
        return (
            <CurrentBreadcrumb leftIcon={item.leftIcon}>
                {item.children}
            </CurrentBreadcrumb>
        );
    }
    if (item.onClick !== undefined) {
        return (
            <Link
                size="sm"
                onClick={item.onClick}
                disabled={item.disabled}
                leftIcon={item.leftIcon}
            >
                {item.children}
            </Link>
        );
    }
    if (item.href !== undefined) {
        return (
            <Link
                size="sm"
                href={item.href}
                disabled={item.disabled}
                leftIcon={item.leftIcon}
            >
                {item.children}
            </Link>
        );
    }
    if (item.render !== undefined) {
        return (
            <Link
                size="sm"
                render={item.render}
                disabled={item.disabled}
                leftIcon={item.leftIcon}
            >
                {item.children}
            </Link>
        );
    }
    return (
        <BreadcrumbText leftIcon={item.leftIcon}>
            <span className={styles.label}>{item.children}</span>
        </BreadcrumbText>
    );
};

/**
 * One level of a `Breadcrumbs` trail: a Link (`onClick`, `href` or `render`),
 * static text, or the `current` page.
 */
export const BreadcrumbItem = (props: BreadcrumbItemProps) => (
    <li
        className={classNames(
            styles.item,
            { [styles.currentItem]: props.current },
            props.className,
        )}
    >
        <BreadcrumbItemContent item={props} />
        <ChevronRightSmallIcon className={styles.separator} aria-hidden />
    </li>
);
